import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Et lite hakk, naar maskinen har det.
 *
 * Telefonen har `components/haptics` i feltappen. En PC har ingenting, og
 * en iPad svarer ikke paa `vibrate` — da skjer det ingenting, og det er
 * riktig: hjulet hakker visuelt uansett.
 */
function notch(): void {
  try {
    navigator.vibrate?.(8);
  } catch {
    /* Noen nettlesere kaster paa vibrate uten brukerhandling. */
  }
}

/**
 * Piksler per hakk. Ett hakk per verdi.
 *
 * 20 er riktig for tidskoden: der er det seksti verdier aa komme gjennom, og
 * hjulet maa kunne spinnes. Der det er FAA verdier er det altfor foelsomt —
 * fingeren glir noen piksler i det den slipper, og hjulet har hoppet videre.
 * Se `notchPx`.
 */
const NOTCH = 20;

/** Hvor mye musehjulet maa rulle for ett hakk. */
const HJUL_PX = 40;

export interface WheelProps {
  readonly label: string;
  readonly value: string;
  readonly prev: string;
  readonly next: string;
  readonly active: boolean;
  readonly size?: "large" | "small";
  /**
   * Hvor langt fingeren maa dra for ett hakk.
   *
   * Settes opp der verdiene er faa og hver av dem er et valg — en
   * deltakerliste paa fire navn skal ikke blas gjennom, den skal pekes paa.
   * Standard er tidskodens egen.
   */
  readonly notchPx?: number;
  /**
   * Verdiene ETTER naboene, til aa fylle vinduet under en dragning.
   *
   * Uten dem staar en tredel av vinduet tomt naar fingeren naermer seg et
   * hakkskifte. Valgfrie, saa et hjul uten dem fortsatt virker — det blir
   * bare et hull igjen.
   */
  readonly prev2?: string;
  readonly next2?: string;
  onStep(direction: 1 | -1): void;
  onFocus(): void;
  /**
   * Verdien kan TASTES inn, ikke bare dras fram.
   *
   * Satt paa tidskoden, der alle fire feltene er tall man leser av en
   * boks. To siffer fyller feltet, og da hopper flata videre — se
   * `onNeste`.
   */
  readonly skrivbar?: boolean;
  /** Hele verdien, skrevet inn. Kalles med tallet, ikke med et steg. */
  onSkrevet?(verdi: number): void;
  /** Feltet er fullt. Flata flytter fokus til det neste. */
  onNeste?(): void;
  /** Gir flata tak i feltet, saa den kan sende fokus hit. */
  settFelt?(el: HTMLInputElement | null): void;
  /** Hoeyeste gyldige verdi + 1. Brukes til aa avvise 99 i et timefelt. */
  readonly tak?: number;
}

/**
 * Hjulet fra tidskodearket.
 *
 * ========  TVILLING AV `apps/mobile/src/components/Wheel.tsx`  ========
 *
 * Kopiert hit 03.11.2026, da tidskoden ogsaa skulle kunne settes fra
 * nettflata: «Se paa den vi har i appen, og kopier den.» Oppfoerselen er
 * innstilt paa enhet over flere runder — hakkavstanden, de fem radene, og
 * regningen som gjoer at ingenting hopper gjennom et hakkskifte — og den
 * skal vaere DEN SAMME to steder.
 *
 * Endres den ene, endres den andre. Grunnen til at den er kopiert og ikke
 * delt, er at `packages/shared` ikke har React: aa dra inn et
 * UI-rammeverk i en ren logikkpakke for én komponent er en stoerre
 * endring enn disse to hundre linjene er verdt i dag.
 *
 * ========  OG DETTE ER DET SOM SKILLER DEM  ========
 *
 * Dragningen er identisk — den er gesten, og den maa vaere den samme. Men
 * en PC har to ting en telefon ikke har, og brukeren 03.11.2026 ville ha
 * begge: «Jeg maa kunne skrolle i hjulene med musa» og «jeg oensker at
 * feltene skal kunne skrives i. Naar et er fylt ut, saa hopper den
 * automatisk til neste felt.»
 *
 * Derfor: `onWheel` paa vinduet, og midtverdien som et felt man kan taste
 * i. Begge er tillegg for en maskin med mus og tastatur; ingen av dem
 * roerer regningen som hakker hjulet.
 *
 * Følger fingeren direkte og hakker på hver verdi. Det aktive feltet løftes
 * til `tc`, verdiene over og under falmer mot `ink-faint`. Kan også stilles
 * med et trykk på verdien over eller under den valgte — et trykk på tallet
 * over setter hjulet til nettopp det tallet.
 *
 * Verdien er absolutt, ikke en differanse: man stiller inn en verdi noen
 * sekunder fram i tid og bekrefter i det kilden treffer den. Se
 * docs/13-design.md.
 */
export function Wheel({
  label, value, prev, next, active, size = "large", notchPx = NOTCH,
  prev2 = "", next2 = "", onStep, onFocus,
  skrivbar = false, onSkrevet, onNeste, settFelt, tak,
}: WheelProps): JSX.Element {
  const [offset, setOffset] = useState(0);
  const [settling, setSettling] = useState(false);
  const drag = useRef<{ id: number; y: number; notched: number } | null>(null);
  /*
   * Om fingeren DRO.
   *
   * Naboverdiene er knapper: et trykk paa tallet over setter hjulet dit. Men
   * en knapp gir ogsaa klikk naar en dragning starter og slutter paa den
   * uten aa flytte seg saa mye at nettleseren mister maalet — og da hoppet
   * hjulet ett hakk i det fingeren slapp, uten at noen hadde trykket paa
   * noe. Det var ikke foelsomhet, det var et klikk.
   */
  const dro = useRef(false);

  const big = size === "large" ? 56 : 34;
  const small = size === "large" ? 19 : 15;
  const row = Math.round(big * 0.72);

  /*
   * ========  MUSEHJULET  ========
   *
   * Lest paa vinduet med en EGEN lytter, ikke `onWheel`: React kobler
   * hjulhendelser passivt, og en passiv lytter kan ikke stanse sidas egen
   * rulling. Uten `preventDefault` ville flata bak rullet mens tallet gikk.
   *
   * SUMMERES OPP. En styreflate sender mange smaa utslag, og ett hakk per
   * hendelse ville spunnet hjulet ut av kontroll. `HJUL_PX` er avstanden
   * ett hakk koster — samme tanke som `notchPx` for fingeren.
   *
   * Retningen foelger systemet: ruller man OPP, gaar tallet opp.
   */
  const vindu = useRef<HTMLDivElement | null>(null);
  const stegRef = useRef(onStep);
  stegRef.current = onStep;
  const fokusRef = useRef(onFocus);
  fokusRef.current = onFocus;

  useEffect(() => {
    const el = vindu.current;
    if (el === null) return;
    let akk = 0;
    const paa = (e: WheelEvent): void => {
      e.preventDefault();
      fokusRef.current();
      akk += e.deltaY;
      while (akk >= HJUL_PX) { akk -= HJUL_PX; stegRef.current(-1); notch(); }
      while (akk <= -HJUL_PX) { akk += HJUL_PX; stegRef.current(1); notch(); }
    };
    el.addEventListener("wheel", paa, { passive: false });
    return () => { el.removeEventListener("wheel", paa); };
  }, []);

  /**
   * Sifrene som er tastet inn, men ikke fullfoert.
   *
   * Ett siffer er ikke et svar: «1» i timefeltet kan bli 1 eller 19. Det
   * vises som det er til det andre sifferet kommer, og da hopper flata
   * videre. Forlater man feltet med ett siffer, gjelder det tallet.
   */
  const [utkast, setUtkast] = useState<string | null>(null);
  /*
   * OG I EN REF, fordi tastetrykk kommer raskere enn React tegner.
   *
   * Leste jeg bufferen fra tilstanden, saa det andre sifferet en VERDI SOM
   * IKKE VAR SATT ENNAA: «1» og «0» ble til to foerstesifre i stedet for
   * tallet ti. Proevd i nettleseren 03.11.2026 — «1005» ble «01:00».
   * Ref-en oppdateres i samme oeyeblikk tasten leses.
   */
  const utkastRef = useRef<string | null>(null);
  const settUtkast = useCallback((v: string | null): void => {
    utkastRef.current = v;
    setUtkast(v);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    // Fangsten holder dragningen selv om fingeren glir utenfor hjulet.
    // Den kan kaste på pekere nettleseren ikke kjenner, og da skal hjulet
    // fortsatt virke — bare uten fangst.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Ingen fangst tilgjengelig. Dragningen går videre uansett.
    }
    drag.current = { id: e.pointerId, y: e.clientY, notched: 0 };
    dro.current = false;
    setSettling(false);
    onFocus();
  }, [onFocus]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (d === null || d.id !== e.pointerId) return;
    /*
     * Å dra NEDOVER øker verdien.
     *
     * Hjulet er tegnet med det høyere tallet over det valgte og det lavere
     * under. Å dra nedover flytter kolonnen ned og henter tallet over inn i
     * midten — altså opp én verdi. Gesten og visningen må peke samme vei,
     * ellers står 00 og går til 23 når fingeren sier 01.
     *
     * Merk at prototypen har to hjul som drar hver sin vei: `spin` i
     * tidskodearket øker oppover, `wspin` på timerflatene nedover. Her er
     * alle hjul like, og de følger `wspin`.
     */
    const travel = e.clientY - d.y;
    // Noen piksler er en finger som ligger stille, ikke en dragning.
    if (Math.abs(travel) > 6) dro.current = true;
    const steps = Math.trunc(travel / notchPx);
    while (d.notched < steps) { d.notched += 1; onStep(1); notch(); }
    while (d.notched > steps) { d.notched -= 1; onStep(-1); notch(); }
    setOffset(travel - d.notched * notchPx);
  }, [onStep, notchPx]);

  const finish = useCallback((): void => {
    if (drag.current === null) return;
    drag.current = null;
    setSettling(true);
    setOffset(0);
  }, []);

  const step = (dir: 1 | -1) => (): void => {
    if (dro.current) {
      dro.current = false;
      return;
    }
    onFocus();
    onStep(dir);
    notch();
  };

  /*
   * ALT ER EN FUNKSJON AV AVSTANDEN TIL MIDTEN.
   *
   * Her laa hoppet. Stoerrelsen ble interpolert med tall som ikke traff
   * maalet: naboen vokste til 1,6 ganger sin egen skrift — tretti piksler —
   * og ble saa midtverdien paa femtiseks i det hakket skiftet. Et byks paa
   * aatti prosent i ett bilde, hver gang et navn naermet seg midten.
   *
   * Naa regnes stoerrelse og gjennomsikt av `d`, avstanden fra midten i
   * rader. Den gaar kontinuerlig gjennom hakkskiftet: raden som naar d = 0
   * er NOEYAKTIG saa stor som midtverdien blir i neste bilde, og raden som
   * naar d = 1 er noeyaktig saa stor som naboen blir. Da finnes det ikke noe
   * sprang aa se.
   */
  const t = offset / notchPx;
  const stoerrelse = (d: number): number => big + (small - big) * Math.min(1, Math.abs(d));
  const gjennomsikt = (d: number): number => {
    const a = Math.abs(d);
    return a <= 1 ? 1 - 0.3 * a : Math.max(0, 0.7 - 0.7 * (a - 1));
  };

  /*
   * FEM RADER, IKKE TRE.
   *
   * Vinduet viser tre, og kolonnen flyttes opptil én rad. Med bare tre rader
   * ble en tredel av vinduet TOMT naar man naermet seg et hakkskifte — den
   * neste verdien fantes ikke ennaa. Ytterradene er der bare for aa fylle
   * det hullet mens fingeren er underveis.
   */
  const rader = [
    { v: prev2, o: -2 }, { v: prev, o: -1 }, { v: value, o: 0 },
    { v: next, o: 1 }, { v: next2, o: 2 },
  ] as const;

  return (
    <div className={`wheel${active ? " wheel--active" : ""}`}>
      <div
        className="wheel__window"
        ref={vindu}
        style={{ height: row * 3, width: size === "large" ? 92 : 64 }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finish}
        onPointerCancel={finish}
      >
        <div
          className={`wheel__column${settling ? " wheel__column--settling" : ""}`}
          /*
           * Midtraden er den tredje, saa kolonnen loeftes én rad. `notchPx`
           * og ikke NOTCH: ett hakk skal flytte den én rad uansett hvor langt
           * fingeren maa dra for hakket.
           */
          style={{ transform: `translateY(${(t - 1) * row}px)` }}
        >
          {rader.map((r) => {
            const d = r.o + t;
            const midt = r.o === 0;
            const ytterst = Math.abs(r.o) === 2;
            const stil = {
              height: row,
              fontSize: big,
              transform: `scale(${stoerrelse(d) / big})`,
              opacity: gjennomsikt(d),
            };

            /*
             * ========  MIDTVERDIEN ER FELTET  ========
             *
             * Brukeren 03.11.2026: «Jeg vil heller ikke at jeg maa skrive i
             * et annet felt for aa skrive inn tidskoden manuelt. Jeg oensker
             * at feltene pilene peker paa skal kunne skrives i. Naar et er
             * fylt ut, saa hopper den automatisk til neste felt.»
             *
             * Det er TALLET man skriver i, ikke en kopi av det ved siden av.
             * Da finnes det bare ett sted verdien bor, og ingen tilstand der
             * to felt kan si hver sin ting.
             *
             * Feltet ser ut som tallet gjorde: ingen ramme, ingen bakgrunn,
             * samme skrift og stoerrelse. Det er fortsatt hjulet — det tar
             * bare imot tastetrykk ogsaa.
             */
            if (midt && skrivbar) {
              /*
               * ========  SIFRENE TAS I `keydown`, IKKE AV FELTET  ========
               *
               * Foerste utgave lot feltet skrive seg selv og leste verdien
               * i `onChange`. Det sprakk paa noe som ikke er feltets feil:
               * en kontrollert verdi tegnes paa nytt mellom to tastetrykk,
               * og da flytter markoeren seg — «0» og «5» ble «50» eller
               * ingenting, avhengig av hvor den landet. Proevd i
               * nettleseren 03.11.2026, og det var ikke til aa stole paa.
               *
               * Naa fanges sifferet FOER feltet faar det: `preventDefault`,
               * og bufferen under styrer hva som vises. Da finnes det ingen
               * markoer aa komme i utakt med.
               *
               * `onChange` staar igjen som reserve for tastaturer som ikke
               * sier hvilken tast det var — iOS har gjort nettopp det.
               */
              const taSiffer = (sifre: string): void => {
                const n = Number(sifre);
                /* ETT siffer er ikke et svar: «1» kan bli 1 eller 19. Men er
                   det saa hoeyt at et siffer til ikke kan gi en gyldig verdi
                   — 7 i et timefelt — er det ferdig. */
                const kanVokse = sifre.length < 2
                  && (tak === undefined || n * 10 < tak);
                if (kanVokse) { settUtkast(sifre); return; }
                settUtkast(null);
                onSkrevet?.(tak !== undefined && n >= tak ? tak - 1 : n);
                onNeste?.();
              };

              return (
                <input
                  key={r.o}
                  ref={settFelt}
                  className="wheel__value wheel__felt mono"
                  /*
                   * ========  ET FELT KLIPPER, EN KNAPP GJOER DET IKKE  ========
                   *
                   * Radhoeyden er 0,72 av skriftstoerrelsen — rikelig for en
                   * `button`, som lar bokstaven henge utenfor boksen sin.
                   * Men et `input` klipper teksten til innholdsboksen, og da
                   * ble bunnen av tallene skaaret av med et par piksler.
                   * Brukeren 03.11.2026: «Tallene er litt kutta i bunn.»
                   *
                   * Boksen faar derfor luft, og marginen tar den tilbake:
                   * ytre hoeyde er fortsatt noeyaktig én rad, saa kolonnen
                   * staar der den skal og hakket flytter like langt som foer.
                   */
                  style={{ ...stil, height: row + 10, margin: "-5px 0" }}
                  value={utkast ?? r.v}
                  inputMode="numeric"
                  maxLength={2}
                  aria-label={label}
                  onFocus={(e) => {
                    /* Nytt felt, ny buffer: det som sto halvskrevet her
                       hoerer til forrige gang. */
                    settUtkast(null);
                    onFocus();
                    e.currentTarget.select();
                  }}
                  onChange={(e) => {
                    const sifre = e.target.value.replace(/\D/g, "");
                    if (sifre === "") { settUtkast(""); return; }
                    taSiffer(sifre.slice(-2));
                  }}
                  onBlur={() => {
                    /* Forlater man feltet med ett siffer, gjelder det. */
                    const b = utkastRef.current;
                    if (b !== null && b !== "") onSkrevet?.(Number(b));
                    settUtkast(null);
                  }}
                  onKeyDown={(e) => {
                    if (/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                      taSiffer(((utkastRef.current ?? "") + e.key).slice(-2));
                      return;
                    }
                    if (e.key === "Backspace" || e.key === "Delete") {
                      e.preventDefault();
                      settUtkast("");
                      return;
                    }
                    if (e.key === "ArrowUp") { e.preventDefault(); onStep(1); }
                    if (e.key === "ArrowDown") { e.preventDefault(); onStep(-1); }
                    /* Tab tar med seg et halvskrevet tall. */
                    if (e.key === "Tab" && utkastRef.current !== null
                      && utkastRef.current !== "") {
                      onSkrevet?.(Number(utkastRef.current));
                      settUtkast(null);
                    }
                  }}
                />
              );
            }

            return (
              <button
                key={r.o}
                type="button"
                className={midt ? "wheel__value" : "wheel__side"}
                style={stil}
                onClick={midt ? onFocus : r.o === -1 ? step(1) : r.o === 1 ? step(-1) : undefined}
                /* Fyllradene er ikke kontroller. De finnes for oeyet. */
                tabIndex={ytterst ? -1 : 0}
                aria-hidden={ytterst}
                aria-label={midt ? `${label} ${value}` : r.o === -1 ? `Øk ${label}` : `Senk ${label}`}
              >
                {r.v}
              </button>
            );
          })}
        </div>
      </div>
      <span className="wheel__label">{label}</span>
    </div>
  );
}
